/**
 * Environment configuration with validation
 * Centralizes all environment variable access
 */

// Environment constants
export const ENVIRONMENTS = {
  LOCAL: 'local',
  DEVELOPMENT: 'dev',
  STAGING: 'stg',
  ACCEPTANCE: 'at',
  PRODUCTION: 'prod'
} as const;

/** Default PostHog instance host */
const DEFAULT_POSTHOG_HOST = 'https://eu.i.posthog.com';

export type Environment = (typeof ENVIRONMENTS)[keyof typeof ENVIRONMENTS];

interface EnvConfig {
  // BrightLocal API Base URL
  apiBaseUrl: string;
  // BrightLocal Data API Base URL (keyword suggestions, etc.)
  dataApiBaseUrl: string;
  // BrightLocal Old Platform Base URL (used for redirecting to the old platform)
  oldPlatformBaseUrl: string;
  // Google OAuth Client ID
  googleOauthClientId: string;
  // Google ReCaptcha Site Key
  googleRecaptchaSiteKey: string;
  // Google API Key
  googleApiKey: string;
  // Google Maps Map ID
  googleMapId: string;
  // Environment Type (local, dev, at, stg, prod)
  environment: Environment;
  // Sentry Configuration
  sentry: {
    // Sentry Data Source Name (DSN) for error reporting
    dsn: string;
    // Whether Sentry error reporting is active
    enabled: boolean;
    // Sentry release version
    release: string;
  };
  // HubSpot Forms API
  hubspot: {
    // HubSpot portal ID
    portalId: string;
    // HubSpot form ID
    formId: string;
  };
  // PostHog Configuration (feature flags and A/B testing)
  posthog: {
    // PostHog project API key (public, safe to expose to the browser)
    key: string;
    // PostHog instance host
    host: string;
    // Whether PostHog is active (requires a key to be set)
    enabled: boolean;
  };
}

const apiBaseUrl = import.meta.env.VITE_BRIGHTLOCAL_API_BASE_URL;
const dataApiBaseUrl = import.meta.env.VITE_DATA_API_BASE_URL;
const oldPlatformBaseUrl = import.meta.env.VITE_OLD_PLATFORM_BASE_URL || '';
const googleApiKey = import.meta.env.VITE_GOOGLE_API_KEY || '';
const googleMapId = import.meta.env.VITE_GOOGLE_MAP_ID || '';
const googleOauthClientId = import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID || '';
const googleRecaptchaSiteKey = import.meta.env.VITE_GOOGLE_RECAPTCHA_SITE_KEY || '';
const sentryDsn = import.meta.env.VITE_SENTRY_DSN || '';
const sentryRelease = import.meta.env.VITE_SENTRY_RELEASE || '';
const hubspotPortalId = import.meta.env.VITE_HUBSPOT_PORTAL_ID || '';
const hubspotFormId = import.meta.env.VITE_HUBSPOT_FORM_ID || '';
const posthogKey = import.meta.env.VITE_PUBLIC_POSTHOG_KEY || '';
const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST || DEFAULT_POSTHOG_HOST;

// Detect environment
const environment = import.meta.env.VITE_ENV as Environment;

// Validate required environment variables
if (!apiBaseUrl || !dataApiBaseUrl) {
  throw new Error('Environment base URLs are required');
}

const SENTRY_ENABLED_ENVIRONMENTS: Environment[] = [
  ENVIRONMENTS.DEVELOPMENT,
  ENVIRONMENTS.STAGING,
  ENVIRONMENTS.ACCEPTANCE,
  ENVIRONMENTS.PRODUCTION
];

export const env: EnvConfig = {
  apiBaseUrl,
  dataApiBaseUrl,
  oldPlatformBaseUrl,
  googleApiKey,
  googleMapId,
  googleOauthClientId,
  googleRecaptchaSiteKey,
  environment,
  sentry: {
    dsn: sentryDsn,
    enabled: SENTRY_ENABLED_ENVIRONMENTS.includes(environment) && !!sentryDsn,
    release: sentryRelease
  },
  hubspot: {
    portalId: hubspotPortalId,
    formId: hubspotFormId
  },
  posthog: {
    key: posthogKey,
    host: posthogHost,
    enabled: !!posthogKey
  }
};
